import { useQuery } from "@tanstack/react-query";
import { Clapperboard, FlaskConical, Image as ImageIcon } from "lucide-react";
import { useState } from "react";
import { api } from "../../api/client";
import type { Modality } from "../../api/types";
import { DataState, PageHeader } from "../../components/ui";
import { PlaygroundGenerationPanel } from "./PlaygroundGenerationPanel";
import { PlaygroundHistory } from "./PlaygroundHistory";
import { PlaygroundLightbox, type LightboxRequest } from "./PlaygroundLightbox";
import { isPlaygroundNotConfigured } from "./playgroundLogic";

// PlaygroundPage lets console users test image/video generation through the server-configured test Consumer.
// 生成测试页：控制台用户通过服务端配置的测试 Consumer 发起图片/视频生成。
export function PlaygroundPage() {
  const [tab, setTab] = useState<Modality>("image");
  const [lightbox, setLightbox] = useState<LightboxRequest | null>(null);
  const capabilities = useQuery({ queryKey: ["playground-capabilities"], queryFn: api.playgroundCapabilities, retry: false });
  const notConfigured = isPlaygroundNotConfigured(capabilities.error);
  const models = capabilities.data?.data.models || [];
  const businessIdentifier = capabilities.data?.data.business_identifier;

  return <>
    <PageHeader title="生成测试" description="用服务端配置的测试 Consumer 测试指定模型或渠道，也可显式测试业务路由策略；成本与请求记录都计入该 Consumer。"
      actions={businessIdentifier ? <span className="readonly-badge">业务标识 {businessIdentifier}</span> : undefined} />
    {notConfigured ? <PlaygroundNotConfigured /> : <DataState loading={capabilities.isLoading} error={capabilities.error}>
      <div className="segmented-control task-tabs playground-tabs" role="group" aria-label="生成类型">
        {([["image", "图片", ImageIcon], ["video", "视频", Clapperboard]] as const).map(([value, label, Icon]) => <button type="button" key={value} aria-pressed={tab === value} className={tab === value ? "active" : ""} onClick={() => setTab(value)}><Icon size={15} />{label}</button>)}
      </div>
      <PlaygroundGenerationPanel modality="image" hidden={tab !== "image"} capabilities={models} testModels={capabilities.data?.data.test_models || []} routingSupported={capabilities.data?.data.routing_version === 1} onPreview={setLightbox} />
      <PlaygroundGenerationPanel modality="video" hidden={tab !== "video"} capabilities={models} testModels={capabilities.data?.data.test_models || []} routingSupported={capabilities.data?.data.routing_version === 1} onPreview={setLightbox} />
      <PlaygroundHistory onPreview={setLightbox} />
    </DataState>}
    {lightbox ? <PlaygroundLightbox items={lightbox.items} startIndex={lightbox.startIndex} title={lightbox.title} onClose={() => setLightbox(null)} /> : null}
  </>;
}

function PlaygroundNotConfigured() {
  return <section className="content-section playground-not-configured" aria-labelledby="playground-not-configured-title">
    <FlaskConical size={28} />
    <div>
      <h2 id="playground-not-configured-title">生成测试尚未启用</h2>
      <p>服务端还没有配置测试 Consumer，或该 Consumer 已停用、没有有效的调用 Key。其他控制台功能不受影响。</p>
      <p className="muted">请网关管理员创建「Moneyking 生成测试」Consumer，授权需要测试的模型，并在部署配置中设置 <code>MODEL_GATEWAY_PLAYGROUND_CONSUMER_ID</code>。</p>
    </div>
  </section>;
}
